import { useGetDetailAssign, useUpdateAssign } from '@vindr/services/actions/assign.action';
import { ParamsUpdateAssign } from '@vindr/services/apis/assign.api';
import { ASSIGNED_TYPE } from '@vindr/services/constants/assigned.constant';
import { MESSAGE } from '@vindr/services/constants/message.constant';
import { PERMISSIONS } from '@vindr/services/constants/permissions.constant';
import keycloak from '@vindr/services/keycloak';
import { Button, Col, Form, Modal, Row, Select } from 'antd';
import { MessageInstance } from 'antd/es/message/interface';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import AccountSearch from '../AccountSearch';
import EditAssign from './EditAssign';
import GroupSearch from './GroupSearch';
import { validateEmptyAssign } from './components/validateEmptyAssign';
import useAccountColumns from './useAccountColumns';
import useGroupColumns from './useGroupColumns';
import { Table } from '@vindr/ui';

const AssignedEditForm = ({
  assign,
  onFinish,
  message,
}: {
  assign: Parameters<typeof EditAssign>['0']['assignData'];
  onFinish: () => void;
  message: MessageInstance;
}) => {
  const { t } = useTranslation('Vindoc');
  const [popupAccountSearch, setPopupAccountSearch] = useState(false);
  const [popupGroupSearch, setPopupGroupSearch] = useState(false);
  const [accountList, setAccountList] = useState([]);
  const [groupList, setGroupList] = useState([]);

  const [assignForm] = Form.useForm();
  const assignType = Form.useWatch('type', assignForm);

  const { mutateAsync: updateAssign } = useUpdateAssign();

  const { data: detailAssign, isFetched } = useGetDetailAssign(assign?.id, {
    enabled: !!assign?.id,
  });

  useEffect(() => {
    setAccountList(detailAssign?.data?.assign_users || []);
    setGroupList(detailAssign?.data?.assign_groups || []);
  }, [detailAssign?.data?.assign_groups, detailAssign?.data?.assign_users, isFetched]);

  const accountColumns = useAccountColumns({
    onRemove: (record) => {
      const newAccounts = [...accountList];
      setAccountList(newAccounts.filter((it) => it?.id !== record?.id));
    },
  });
  const groupColumns = useGroupColumns({
    onRemove: (record) => {
      const newGroups = [...groupList];
      setGroupList(newGroups.filter((it) => it?.id !== record?.id));
    },
  });

  const handleSave = () => {
    if (accountList.length === 0 && groupList.length === 0) {
      return validateEmptyAssign({ assignType, message });
    }

    assignForm
      .validateFields()
      .then(() => {
        const input: ParamsUpdateAssign = {
          id: assign?.id,
          assign_ids:
            assignType === ASSIGNED_TYPE.USER
              ? accountList.map((it) => it?.id) || []
              : groupList.map((it) => it?.id) || [],
          type: assignType,
        };

        updateAssign(input, {
          onSuccess: () => {
            message.success(t(MESSAGE.SUCCESS.SCD_17002));
            onFinish();
          },
          onError: (error: any) => {
            const error_message =
              (error?.response?.data?.error_code &&
                MESSAGE.ERROR[error?.response?.data?.error_code]) ||
              'Update error';

            message.error(t(error_message));
          },
        });
      })
      .catch((info) => {
        console.log('Validate Failed:', info);
      });
  };

  useEffect(() => {
    assignForm.setFieldValue('type', assign?.type || ASSIGNED_TYPE.USER);
  }, [assign?.type, assignForm]);

  return (
    <div className="flex w-full flex-col">
      <div className="flex flex-col">
        <div className="flex flex-col">
          <Form
            // layout="vertical"
            requiredMark
            form={assignForm}
            name="form"
            onFinish={handleSave}
            className="form-filter-condition"
            labelCol={{ span: 9 }}
            wrapperCol={{ span: 15 }}
            labelAlign="left"
          >
            <Row gutter={[8, 8]}>
              <Col className="gutter-row" span={24}>
                <Form.Item
                  name="type"
                  label={t('Assign Type')}
                  className="mb-2"
                  labelCol={{ span: 3 }}
                  wrapperCol={{ span: 21 }}
                  required
                >
                  <Select placeholder={t('Select Type')} size="small" disabled>
                    <Select.Option
                      key={ASSIGNED_TYPE.USER}
                      value={ASSIGNED_TYPE.USER}
                      title={t('User')}
                    >
                      {t('User')}
                    </Select.Option>
                    <Select.Option
                      key={ASSIGNED_TYPE.GROUP}
                      value={ASSIGNED_TYPE.GROUP}
                      title={t('Group')}
                    >
                      {t('Group')}
                    </Select.Option>
                  </Select>
                </Form.Item>
              </Col>
            </Row>
          </Form>
        </div>
      </div>

      {assignType === ASSIGNED_TYPE.USER ? (
        <>
          <div className="flex items-center justify-between">
            <div className="py-2 text-xl">{t(`Account List`)}</div>
            {/* <Button onClick={() => setPopupAccountSearch(true)}>{t('Add')}</Button> */}
          </div>

          <Table
            columns={accountColumns}
            dataSource={accountList}
            scroll={{ y: 500 }}
            pagination={null}
            wrapperClassName="h-auto"
          />
          <Button className="w-full" onClick={() => setPopupAccountSearch(true)}>
            {t('Add')}
          </Button>
        </>
      ) : (
        <>
          <div className="flex items-center justify-between">
            <div className="py-2 text-xl">{t(`Group Account List`)}</div>
            {/* <Button onClick={() => setPopupGroupSearch(true)}>{t('Add')}</Button> */}
          </div>

          <Table
            columns={groupColumns}
            dataSource={groupList}
            scroll={{ y: 500 }}
            pagination={null}
            wrapperClassName="h-auto"
          />
          <Button className="w-full" onClick={() => setPopupGroupSearch(true)}>
            {t('Add')}
          </Button>
        </>
      )}

      <Modal
        className="vindr-modal procedure-search max-w-screen w-[1200px] max-sm:top-[10px]"
        title={t('Select Account')}
        open={popupAccountSearch}
        maskClosable={false}
        destroyOnClose
        zIndex={1001}
        onCancel={() => setPopupAccountSearch(false)}
        footer={null}
      >
        <AccountSearch
          initFilters={{
            institutions: assign?.order?.institution?.code
              ? [assign?.order?.institution?.code]
              : [],
          }}
          selectedAccounts={accountList}
          onCancel={() => setPopupAccountSearch(false)}
          onSave={(accounts) => {
            setAccountList(accounts);
            setPopupAccountSearch(false);
          }}
        />
      </Modal>

      <Modal
        className="vindr-modal procedure-search max-w-screen w-[1200px]"
        title={t('Select Group Account')}
        open={popupGroupSearch}
        maskClosable={false}
        destroyOnClose
        zIndex={1001}
        onCancel={() => setPopupGroupSearch(false)}
        footer={null}
      >
        <GroupSearch
          initFilters={{
            institution_ids: assign?.order?.institution_id ? [assign?.order?.institution_id] : [],
          }}
          selectedAccounts={accountList}
          onCancel={() => setPopupGroupSearch(false)}
          onSave={(groups) => {
            setGroupList(groups);
            setPopupGroupSearch(false);
          }}
        />
      </Modal>

      <div className="footer mt-3 flex flex-wrap items-end justify-end space-y-2">
        <Button onClick={onFinish}>{t('Cancel')}</Button>
        <Button
          type="primary"
          onClick={handleSave}
          className="ml-2"
          disabled={!keycloak.hasOneOfPerm([PERMISSIONS.assigned.admin_assignedlist])}
        >
          {t('Save')}
        </Button>
      </div>
    </div>
  );
};

export default AssignedEditForm;
